import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../../../redux/store";
import { Share2 } from "lucide-react";
import { fetchPages } from "../../../../../../../redux/blogSlice";
import SmartFooter from "../../../../../../../GLOBAL/SmartFooter";

const gold = "#0037ffff";

const EPageContent: React.FC = () => {
  const { slug } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { pages } = useSelector((state: RootState) => state.blog);
  const page = pages.find((p) => p.slug === slug);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!pages.length) dispatch(fetchPages());
  }, [dispatch, pages.length]);

  const handleShare = () => {
    if (!page) return;
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: page.title, url });
    else {
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard");
    }
  };

  if (!page)
    return <p style={{ color: "#fff", padding: 24 }}>Loading page...</p>;

  const isHtmlContent = /<\/?[a-z][\s\S]*>/i.test(page.content);

  const emailRegex = /([a-zA-Z0-9._%+-]+@areluxe\.com)/gi;
  const linkRegex = /(https?:\/\/[^\s]+)/gi;

  const parseParagraph = (text: string) =>
    text
      .replace(
        emailRegex,
        `<a href="mailto:$1" style="color:${gold}; font-weight:600; text-decoration:none;">$1</a>`
      )
      .replace(
        linkRegex,
        (url) =>
          `<a href="${url}" target="_blank" style="color:${gold}; font-weight:600; text-decoration:underline;">${url}</a>`
      );

  const renderParagraph = (para: string, idx: number) => {
    const trimmed = para.trim();
    const isTitle =
      /^[A-Z][A-Za-z\s]{3,}$/.test(trimmed) && trimmed.length < 80;

    return (
      <p
        key={idx}
        style={{
          marginBottom: 20,
          fontWeight: isTitle ? 700 : 400,
          fontSize: isTitle ? "20px" : "16px",
          color: isTitle ? gold : "#eee",
        }}
        dangerouslySetInnerHTML={{ __html: parseParagraph(trimmed) }}
      />
    );
  };

  return (
    <div style={styles.page}>
      {/* CENTERED CONTENT */}
      <div style={styles.wrapper}>
        <div
          style={{
            ...styles.card,
            padding: isMobile ? "25px 20px 35px" : "35px 35px 50px",
          }}
        >
          {/* HEADER */}
          <div style={styles.header}>
            <h1 style={styles.title}>{page.title}</h1>
            <Share2
              size={22}
              color={gold}
              onClick={handleShare}
              style={{ cursor: "pointer" }}
            />
          </div>

          {/* CONTENT */}
          <div
            style={{
              columnCount: isMobile ? 1 : 2,
              columnGap: isMobile ? "0px" : "40px",
              lineHeight: 1.8,
              fontSize: 16,
              color: "#ddd",
            }}
          >
            {isHtmlContent ? (
              <div dangerouslySetInnerHTML={{ __html: page.content }} />
            ) : (
              page.content.split(/\n{2,}/).map(renderParagraph)
            )}
          </div>
        </div>
      </div>

      {/* FULL WIDTH FOOTER (same as homepage) */}
      <div style={{ marginTop: 80 }}>
        <SmartFooter />
      </div>
    </div>
  );
};

export default EPageContent;

const styles: Record<string, React.CSSProperties> = {
page: {
  background: "linear-gradient(180deg,#0a0a0a,#111)",
  minHeight: "100vh",
  paddingTop: 50,
  color: "#fff",
  overflowX: "hidden",   // ⭐ important fix
},
wrapper: {
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  padding: "0 30px",   // ⭐ smaller padding for mobile
  boxSizing: "border-box",
},
  card: {
    background: "linear-gradient(145deg,#0c0c0c,#161616)",
    borderRadius: 25,
    border: "1px solid rgba(191,155,48,0.25)",
    boxShadow:
      "0 8px 25px rgba(0,0,0,0.6), inset 0 0 18px rgba(191,155,48,0.12)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 30,
  },
  title: {
    fontSize: 32,
    fontWeight: 900,
    color: gold,
    textShadow:
      "0 0 6px rgba(191,155,48,0.5), 0 0 12px rgba(191,155,48,0.25)",
  },
};